import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Mic, Play, UserPlus } from "lucide-react";
import { SubHeader } from "@/components/SubHeader";
import { Reveal, SectionTag } from "@/components/Reveal";
import { API } from "@/lib/site";

export default function Connect() {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Healthcare Connect | Healthcare Solutions";
    return () => {
      document.title = "Healthcare Solutions | Connecting Healthcare. Creating Solutions.";
    };
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/episodes`)
      .then(({ data }) => setEpisodes(data))
      .catch(() => setEpisodes([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="connect-page" className="min-h-screen bg-white">
      <SubHeader crumb="Healthcare Connect" />
      <main className="mx-auto max-w-7xl px-4 pb-24 pt-32 sm:px-6 lg:px-8">
        <Reveal>
          <SectionTag index="HC" label="Healthcare Connect" />
          <h1 className="font-display mt-6 max-w-3xl text-3xl font-extrabold tracking-tight text-[#0B3C5C] sm:text-4xl lg:text-5xl">
            Conversations That <span className="text-gradient-teal">Shape Healthcare.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#4A6B82] sm:text-lg">
            Interviews and industry discussions with hospital directors, doctors, healthcare
            administrators, entrepreneurs and insurance professionals across the healthcare ecosystem.
          </p>
        </Reveal>

        {loading ? (
          <p className="mt-20 text-center text-sm text-[#6B8AA0]">Loading episodes...</p>
        ) : episodes.length === 0 ? (
          <Reveal delay={0.1}>
            <div className="mt-16 flex flex-col items-center rounded-[2rem] border border-[#0B3C5C]/10 bg-[#EAF3F8]/60 px-8 py-20 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#5FA832]/10">
                <Mic className="h-7 w-7 text-[#4C8F2F]" />
              </span>
              <h2 className="font-display mt-6 text-xl font-bold text-[#0B3C5C]">First conversations coming soon</h2>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-[#4A6B82]">
                We are lining up conversations with healthcare leaders across Madhya Pradesh,
                Chhattisgarh and Maharashtra. If you work in healthcare and have a perspective
                worth sharing, we would love to feature you.
              </p>
              <a
                href="/#enquiry"
                data-testid="connect-empty-guest-cta"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#5FA832] px-8 py-4 text-sm font-bold text-white transition-all duration-300 hover:bg-[#4C8F2F]"
              >
                <UserPlus className="h-4 w-4" />
                Become a Featured Guest
              </a>
            </div>
          </Reveal>
        ) : (
          <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {episodes.map((ep, i) => (
              <motion.div
                key={ep.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
              >
                <Link
                  to={`/connect/${ep.slug}`}
                  data-testid={`episode-card-${ep.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-[#0B3C5C]/10 bg-[#EAF3F8]/60 transition-all duration-300 hover:-translate-y-1 hover:border-teal-500/30 hover:shadow-xl hover:shadow-teal-900/20"
                >
                  <div className="relative h-48 overflow-hidden bg-[#EAF3F8]">
                    {ep.cover_image ? (
                      <img src={ep.cover_image} alt={ep.title} loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    ) : (
                      <div className="flex h-full items-center justify-center">
                        <Mic className="h-10 w-10 text-teal-500/40" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#060D17]/90 via-transparent to-transparent" />
                    {ep.youtube_url && (
                      <span className="absolute bottom-4 right-4 flex h-10 w-10 items-center justify-center rounded-full bg-[#5FA832] shadow-lg shadow-teal-500/30">
                        <Play className="h-4 w-4 text-white" fill="currentColor" />
                      </span>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h2 className="font-display text-lg font-bold leading-snug text-[#0B3C5C] transition-colors group-hover:text-[#5FA832]">
                      {ep.title}
                    </h2>
                    <p className="font-mono-x mt-3 text-[10px] uppercase tracking-[0.2em] text-[#4C8F2F]">
                      {ep.guest_name}{ep.guest_role ? ` · ${ep.guest_role}` : ""}
                    </p>
                    {ep.description && (
                      <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-[#4A6B82]">{ep.description}</p>
                    )}
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
